import * as React from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { getDownloadURL, ref } from 'firebase/storage';
import { storage } from './firebase';
import {
	Box,
	CircularProgress,
	Typography,
	IconButton,
	Toolbar,
	Paper,
	Divider,
	Grid,
	Card,
	CardContent,
} from '@mui/material';
import {
	ZoomIn,
	ZoomOut,
    Replay,
	ViewModule,
	ViewStream,
} from '@mui/icons-material';

// PDF.js worker の設定（ローカルファイルを使用）
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

export interface pdfViewerProps {
    props:{
        pdfPath: string,
    }
}

/**
 * PDFプレビュー
 */
const PdfViewer:React.FC<pdfViewerProps> = ({props}) => {
    const [storagePath, setStragePath] = React.useState('');
    const [show, setShow] = React.useState('none');
    const [numPages, setNumPages] = React.useState<number>(0);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string>('');
    const [scale, setScale] = React.useState<number>(1.0);
    const [currentPage, setCurrentPage] = React.useState<number>(1);
    const [showThumbnails, setShowThumbnails] = React.useState<boolean>(false);

    // Firebase Storageからダウンロード URL を取得
    React.useEffect(() => {
        if (props.pdfPath) {
            setShow('block');
            setLoading(true);
            setError('');
            const gsReference = ref(storage, props.pdfPath);
        
            getDownloadURL(gsReference)
            .then((url) => {
                setStragePath(url)
                setLoading(false);
            })
            .catch((err) => {
                console.log(err);
                setError('PDFの読み込みに失敗しました');
                setLoading(false);
            });
        } else {
            setShow('none');
        }
    }, [props.pdfPath]);

    // PDF読み込み成功時の処理
    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
    };

    // PDF読み込みエラー時の処理
    const onDocumentLoadError = (error: Error) => {
        console.error('PDF読み込みエラー:', error);
        setError('PDFの読み込みに失敗しました');
    };

    // ズーム機能
    const handleZoomIn = () => {
        setScale(prev => Math.min(prev + 0.25, 3.0));
    };

    const handleZoomOut = () => {
        setScale(prev => Math.max(prev - 0.25, 0.5));
    };

    const handleZoomReset = () => {
        setScale(1.0);
    };

    // サムネイル表示切り替え
    const toggleThumbnails = () => {
        setShowThumbnails(prev => !prev);
    };

    // ページ幅の計算
    const getPageWidth = () => {
        const containerWidth = showThumbnails ? 
            Math.min(window.innerWidth * 0.65, 600) : 
            Math.min(window.innerWidth - 100, 800);
        return containerWidth * scale;
    };

    // サムネイル用のページ幅
    const getThumbnailWidth = () => {
        return 150;
    };

    // サムネイルクリック時の処理
    const handleThumbnailClick = (pageNumber: number) => {
        setCurrentPage(pageNumber);
        // メインビューの該当ページにスクロール
        const pageElement = document.getElementById(`page-${pageNumber}`);
        if (pageElement) {
            pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    // 全ページを配列として生成
    const renderAllPages = () => {
        const pages = [];
        for (let i = 1; i <= numPages; i++) {
            pages.push(
                <Box key={i} id={`page-${i}`} sx={{ mb: 2, border: currentPage === i ? '2px solid #1976d2' : 'none' }}>
                    <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mb: 1 }}>
                        ページ {i} / {numPages}
                    </Typography>
                    <Page
                        pageNumber={i}
                        width={getPageWidth()}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        onLoadSuccess={() => {
                            // ページが表示されたら現在のページを更新
                            const observer = new IntersectionObserver(
                                (entries) => {
                                    entries.forEach((entry) => {
                                        if (entry.isIntersecting) {
                                            setCurrentPage(i);
                                        }
                                    });
                                },
                                { threshold: 0.5 }
                            );
                            const element = document.getElementById(`page-${i}`);
                            if (element) {
                                observer.observe(element);
                            }
                        }}
                    />
                </Box>
            );
        }
        return pages;
    };

    // サムネイル表示
    const renderThumbnails = () => {
        const thumbnails = [];
        for (let i = 1; i <= numPages; i++) {
            thumbnails.push(
                <Card 
                    key={i} 
                    sx={{ 
                        mb: 1, 
                        cursor: 'pointer',
                        border: currentPage === i ? '2px solid #1976d2' : '1px solid #e0e0e0',
                        '&:hover': {
                            boxShadow: 3
                        }
                    }}
                    onClick={() => handleThumbnailClick(i)}
                >
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mb: 1 }}>
                            {i}
                        </Typography>
                        <Page
                            pageNumber={i}
                            width={getThumbnailWidth()}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                        />
                    </CardContent>
                </Card>
            );
        }
        return thumbnails;
    };

    return (
        <Box sx={{ 
            display: show, 
            width: '100%', 
            height: '980px',
            position: 'relative',
            overflow: 'hidden'
        }}>
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <CircularProgress />
                    <Typography sx={{ ml: 2 }}>PDFを読み込み中...</Typography>
                </Box>
            )}
            
            {error && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <Typography color="error">{error}</Typography>
                </Box>
            )}

            {storagePath && !loading && !error && (
                <Box>
                    <Document
                        file={storagePath}
                        onLoadSuccess={onDocumentLoadSuccess}
                        onLoadError={onDocumentLoadError}
                        loading={
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                                <CircularProgress />
                                <Typography sx={{ ml: 2 }}>PDFを読み込み中...</Typography>
                            </Box>
                        }
                    >
                        {numPages > 0 && (
                            <Box>
                                {/* ツールバー */}
                                <Paper 
                                    elevation={2} 
                                    sx={{ 
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 1000,
                                        mb: 2,
                                        marginBottom: '0px'
                                    }}
                                >
                                    <Toolbar sx={{ justifyContent: 'space-between', '@media (min-width:600px)': {minHeight: 'auto'} }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <IconButton 
                                                onClick={toggleThumbnails}
                                                color={showThumbnails ? 'primary' : 'default'}
                                            >
                                                {showThumbnails ? <ViewStream /> : <ViewModule />}
                                            </IconButton>
                                            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                                            <Typography variant="body2">
                                                ページ {currentPage} / {numPages}
                                            </Typography>
                                            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                                            <Typography variant="body2">
                                                ズーム: {Math.round(scale * 100)}%
                                            </Typography>
                                        </Box>
                                        
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <IconButton onClick={handleZoomOut} disabled={scale <= 0.5}>
                                                <ZoomOut />
                                            </IconButton>
                                            <IconButton onClick={handleZoomReset}>
                                                <Replay />
                                            </IconButton>
                                            <IconButton onClick={handleZoomIn} disabled={scale >= 3.0}>
                                                <ZoomIn />
                                            </IconButton>
                                        </Box>
                                    </Toolbar>
                                </Paper>

                                {/* メインコンテンツ */}
                                <Box sx={{ 
                                    height: 'calc(100% - 80px)', 
                                }}>
                                    <Grid container>
                                        {/* サムネイル表示 */}
                                        {showThumbnails && (
                                            <Grid item xs={12} md={2}>
                                                <Paper 
                                                    elevation={1} 
                                                    sx={{
                                                        p: 1, 
                                                        height: '900px',
                                                        overflowY: 'auto',
                                                        position: 'sticky',
                                                        top: 0
                                                    }}
                                                >
                                                    <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
                                                        サムネイル
                                                    </Typography>
                                                    {renderThumbnails()}
                                                </Paper>
                                            </Grid>
                                        )}
                                        
                                        {/* PDFメインビュー */}
                                        <Grid item xs={12} md={showThumbnails ? 10 : 12}>
                                            <Box sx={{ 
                                                display: 'flex', 
                                                flexDirection: 'column', 
                                                alignItems: 'center',
                                                gap: 1,
                                                width: '100%',
                                                height: '900px',
                                                overflow: 'auto'
                                            }}>
                                                {renderAllPages()}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Box>
                        )}
                    </Document>
                </Box>
            )}
        </Box>
    );
}

export default PdfViewer;
